import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { client } from '../lib/sanity';
import { newsArticles } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const POSTS_QUERY = `*[_type == "post"] | order(publishedAt desc) {
  _id, title, slug, category, excerpt, publishedAt
}`;

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!client) {
      setLoading(false);
      return;
    }
    client
      .fetch(POSTS_QUERY)
      .then((data) => {
        setPosts(data?.length ? data : null);
        setLoading(false);
      })
      .catch(() => {
        setPosts(null);
        setLoading(false);
      });
  }, []);

  // Use Sanity posts if available, otherwise fall back to mock data
  const articles = posts
    ? posts.map((p) => ({
        id: p._id,
        slug: p.slug?.current,
        category: p.category || 'Insight',
        title: p.title,
        date: p.publishedAt
          ? new Date(p.publishedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })
          : '',
        excerpt: p.excerpt || '',
      }))
    : newsArticles.map((a) => ({ ...a, slug: null }));

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.querySelector('#contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const categoryColor = (cat, idx) => {
    const green = ['Leadership', 'Performance'];
    if (green.includes(cat)) return { bg: 'rgba(27,61,42,0.2)', border: 'rgba(27,61,42,0.5)', text: '#4ade80' };
    return { bg: 'rgba(200,16,46,0.12)', border: 'rgba(200,16,46,0.4)', text: '#ff6b7a' };
  };

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      <Navbar onContactClick={scrollToContact} />

      <section style={{ paddingTop: '140px', paddingBottom: '120px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 40px' }}>
          {/* Header */}
          <div style={{ marginBottom: '64px' }}>
            <Link
              to="/"
              style={{
                color: 'rgba(255,255,255,0.4)',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '13px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '32px',
                transition: 'color 0.2s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11 7H3M3 7L7 3M3 7L7 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Home
            </Link>
            <span style={{
              color: 'rgba(255,255,255,0.4)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '12px',
              fontWeight: '500',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: '16px',
            }}>Blog</span>
            <h1 style={{
              color: '#ffffff',
              fontFamily: "'Climate Crisis', sans-serif",
              fontSize: 'clamp(32px, 5vw, 56px)',
              fontWeight: '400',
              lineHeight: '1.1',
              margin: 0,
            }}>
              Thoughts on Sport, Governance & Leadership
            </h1>
          </div>

          {/* Loading */}
          {loading && (
            <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Figtree', sans-serif", fontSize: '14px' }}>
              Loading posts...
            </p>
          )}

          {/* Articles list */}
          {!loading && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {articles.map((article, index) => {
                const colors = categoryColor(article.category, index);
                const inner = (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 1fr auto',
                      gap: '32px',
                      alignItems: 'start',
                      padding: '36px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    className="article-row"
                  >
                    <div>
                      <div style={{
                        display: 'inline-block',
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '20px',
                        padding: '4px 10px',
                        marginBottom: '12px',
                      }}>
                        <span style={{
                          color: colors.text,
                          fontFamily: "'Figtree', sans-serif",
                          fontSize: '10px',
                          fontWeight: '700',
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                        }}>
                          {article.category}
                        </span>
                      </div>
                      <div style={{
                        color: 'rgba(255,255,255,0.3)',
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: '12px',
                      }}>
                        {article.date}
                      </div>
                    </div>

                    <div>
                      <h3 style={{
                        color: '#ffffff',
                        fontFamily: "'Climate Crisis', sans-serif",
                        fontSize: 'clamp(16px, 2vw, 22px)',
                        fontWeight: '400',
                        lineHeight: '1.3',
                        marginBottom: '12px',
                      }}>
                        {article.title}
                      </h3>
                      <p style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontFamily: "'Figtree', sans-serif",
                        fontSize: '14px',
                        lineHeight: '1.7',
                        margin: 0,
                      }}>
                        {article.excerpt}
                      </p>
                    </div>

                    <div style={{ paddingTop: '4px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M3 11L11 3M11 3H5M11 3V9" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                );

                return article.slug ? (
                  <Link key={article.id} to={`/blog/${article.slug}`} style={{ textDecoration: 'none' }}>
                    {inner}
                  </Link>
                ) : (
                  <div key={article.id}>{inner}</div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .article-row {
            grid-template-columns: 1fr !important;
            gap: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BlogPage;
